import { Component, h, Host, Prop, Element } from '@stencil/core';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import axios from '../../../utils/get-axios';
import { v4 as uuidv4 } from 'uuid';

@Component({
  tag: 'lilu-swiper',
  styleUrl: 'lilu-swiper.scss',
  shadow: false,
})
export class LiluSwiper {
  @Prop() type = 'default';
  @Prop() headerColor = '';
  @Prop() buttonType = '';

  @Prop() items: string;

  itemsArray;

  // https://test.livique.ch/toptipwebservices/products/6673014,6811211,6685728,6323747,6658985,6664634,5965697,5965697,6289905,6088151?lang=de

  @Element() el: HTMLElement;

  swiperObj: Swiper;

  swiperId = uuidv4();
  swiperSelector = `swiper-id-${this.swiperId}`;
  swiperPagination = `swiper-pagination-${this.swiperId}`;
  swiperNextEl = `swiper-button-next-${this.swiperId}`;
  swiperPrevEl = `swiper-button-prev-${this.swiperId}`;

  defaultOptions: any = {
    modules: [Navigation, Pagination],
    loop: false,
    speed: 400,
    preloadImages: true,
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 0,
    navigation: {
      nextEl: `#${this.swiperNextEl}`,
      prevEl: `#${this.swiperPrevEl}`,
    },
    noSwipingClass: 'swiper-no-swiping',
    // Responsive breakpoints
    threshold: 5,
    on: {
      slideChange: () => this.handleSlideChange(),
    },
  };

  componentWillLoad() {
    if (this.items) {
      const jsonObj = JSON.parse(this.items);
      this.itemsArray = new Array();
      for (let i in jsonObj) {
        this.itemsArray.push(jsonObj[i]);
      }
    }
  }

  async fetchProducts(productIds: any[]): Promise<any> {
    const ids = productIds.join(',');

    const url = `/toptipwebservices/products/${ids}?lang=${document.documentElement.lang}`;

    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }

  isSliderMovingForward() {
    return this.swiperObj.activeIndex > this.swiperObj.previousIndex;
  }

  getTilesToBeLoaded(code: string): NodeListOf<HTMLLiluProductTileElement> {
    return document.querySelectorAll(`lilu-product-tile[product-to-load="${code}"`); // preload all tiles for this product in the whole page, to save request
  }

  calculateSlidesToBeLoaded(slideIndex, slidesToLoad) {
    const arrayToSend = this.itemsArray.slice(slideIndex, slideIndex + slidesToLoad);
    const codes: string[] = arrayToSend.map(item => item.code);

    const filteredCodes = codes.filter((code: string) => {
      return this.getTilesToBeLoaded(code).length > 0;
    });

    return filteredCodes;
  }

  loadNextSlides(slideIndex, slidesToLoad) {
    if (!this.itemsArray) return;

    const arrayToSend = this.calculateSlidesToBeLoaded(slideIndex, slidesToLoad);

    if (arrayToSend.length === 0) return;

    this.fetchProducts(arrayToSend)
      .then(productsData => {
        productsData?.forEach(productData => {
          this.getTilesToBeLoaded(productData.code).forEach(tile => {
            tile.setJSONData(productData);
          });
        });
      })
      .catch(error => console.error(error));
  }

  handleSlideChange() {
    if (this.isSliderMovingForward()) {
      this.loadNextSlides(this.swiperObj.activeIndex, this.swiperObj.params.slidesPerView);
    }
  }

  addEmptySlides() {
    if (!this.itemsArray) return;

    const swiperWrapper = this.el.querySelector('.swiper-wrapper');
    const fixSlide = swiperWrapper.querySelector('lilu-fix-product-tile')?.cloneNode(true);

    swiperWrapper.innerHTML = ''; // clean swiper content. It cannot contain sliders for the counters to work

    if (fixSlide) {
      const slide = document.createElement('div');
      slide.classList.add('swiper-slide');
      slide.appendChild(fixSlide);
      swiperWrapper.appendChild(slide);
    }

    let emptySlides = '';
    this.itemsArray.forEach(item => {
      emptySlides += `<div class="swiper-slide"><lilu-product-tile product-to-load='${item.code}'></lilu-product-tile></div>`;
    });

    swiperWrapper.innerHTML += emptySlides;
  }

  componentDidLoad() {
    let slides: number;

    switch (this.type) {
      case 'fix':
        slides = 3;
        break;
      case 'five':
        slides = 5;
        break;
      case 'modal':
        slides = 3;
        break;
      default:
        slides = 4;
        break;
    }

    const swiperOptions = {
      ...this.defaultOptions,
      slidesPerView: slides,
      slidesPerGroup: slides,
      pagination: {
        el: `#${this.swiperPagination}`,
        type: 'fraction',
      },
      breakpoints: {
        0: { slidesPerView: 2, slidesPerGroup: 2 },
        768: { slidesPerView: 3, slidesPerGroup: 3 },
        1200: { slidesPerView: slides, slidesPerGroup: slides },
      },
    };
    this.addEmptySlides();

    this.swiperObj = new Swiper(`#${this.swiperSelector}`, swiperOptions);

    this.loadFirstSlides(swiperOptions.breakpoints);
  }

  getSlidesPerGroupFromBreakpoints(breakpoints): number {
    const viewportWidth = window.innerWidth;
    let slidesPerGroup = 0;
    for (let breakpoint in breakpoints) {
      if (viewportWidth > parseInt(breakpoint)) {
        slidesPerGroup = breakpoints[breakpoint].slidesPerGroup;
      } else {
        return slidesPerGroup;
      }
    }
    return slidesPerGroup;
  }

  loadFirstSlides(breakpoints) {
    if (!this.itemsArray) return;
    this.loadNextSlides(0, this.getSlidesPerGroupFromBreakpoints(breakpoints));
  }

  render() {
    return (
      <Host>
        <div class="swiper-link">
          <slot name="link"></slot>
        </div>
        <div class={`swiper-type-${this.type}`}>
          <slot name="fixed-overlay"></slot>
          <div class={`mySwiper swiper-product swiper-${this.type}`}>
            <lilu-hr></lilu-hr>
            <div class="swiper" id={this.swiperSelector}>
              <div class="swiper-wrapper">
                <slot></slot>
              </div>
              <div class="swiper-navigation">
                <div class="swiper-pagination" id={this.swiperPagination}></div>
                <div class="swiper-button-prev" id={this.swiperPrevEl}></div>
                <div class="swiper-button-next" id={this.swiperNextEl}></div>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
